import { useEffect } from 'react';

import { get } from '../../api';
import T from '../../translate';
import { useGlobalStore } from '../state/globalStore';
import { ENTITY_TYPE, EVENT_FIELDS, LC_ENTITY_FIELDS, SHOWCASE_FIELDS } from '../utils/constants';
import {
  getEntityCookiePassword,
  getEntityOwnerIdFromUserUri,
  getLeadCaptureUrl,
} from '../utils/helper';
import { reportErrorToPlayer } from '../utils/player-controller';
import { ERROR_CODES } from '../utils/player-controller/constants';
import { useNotification } from './useNotification';

export const useEntity = () => {
  const entityId = useGlobalStore((store) => store.apiEntityId);
  const entityType = useGlobalStore((store) => store.entityType);
  const { show: showNotification } = useNotification();

  const getLiveEventEntityData = async (entityId: string) => {
    try {
      const entityPassword = getEntityCookiePassword(entityId, ENTITY_TYPE.EVENT);
      /** TODO: Move this also to use values from EntityLeadCaptureFormResponse's entity parameter. */
      const finalResponse = await get(
        `/live_events/${entityId}?fields=${EVENT_FIELDS}&password=${entityPassword}`,
      );

      useGlobalStore.setState({
        entityOwnerId: getEntityOwnerIdFromUserUri(finalResponse['user']['uri']),
        entity: {
          ...useGlobalStore.getState().entity,
          schedule: finalResponse['schedule'],
          title: finalResponse['title'],
          privacy: finalResponse['streamPrivacy'],
        },
      });
    } catch (error) {
      showNotification({ message: T.UhOhThereWasAProblem });
    }
  };

  const getVideoEntityData = async (entityId: string) => {
    try {
      const entityPassword = getEntityCookiePassword(entityId, ENTITY_TYPE.VIDEO);
      let finalResponse;
      const url = getLeadCaptureUrl(entityPassword, false, LC_ENTITY_FIELDS);
      finalResponse = await get(url);
      finalResponse = finalResponse?.['entityInfo'];
      useGlobalStore.setState({
        entityOwnerId: getEntityOwnerIdFromUserUri(finalResponse?.['user']?.['uri']),
        entity: {
          ...useGlobalStore.getState().entity,
          title: finalResponse?.['title'],
          thumbnailUrl: finalResponse['pictures'] ? finalResponse?.['pictures']?.['baseLink'] : '',
          privacy: finalResponse['privacy'],
          duration: finalResponse['duration'],
        },
      });
    } catch (error) {
      reportErrorToPlayer({
        code: ERROR_CODES.ENTITY_DATA_MISSING,
        message: 'Error fetching entity data',
      });
      showNotification({ message: T.UhOhThereWasAProblem });
    }
  };

  const getAlbumEntityData = async (entityId: string) => {
    try {
      const entityPassword = getEntityCookiePassword(entityId, ENTITY_TYPE.SHOWCASE);
      const finalResponse = await get(
        `/albums/${entityId}?fields=${SHOWCASE_FIELDS}&password=${entityPassword}`,
      );

      useGlobalStore.setState({
        entityOwnerId: getEntityOwnerIdFromUserUri(finalResponse['user']['uri']),
        entity: {
          ...useGlobalStore.getState().entity,
          title: finalResponse['name'],
          privacy: finalResponse['privacy'],
          thumbnailUrl: finalResponse['pictures'] ? finalResponse?.['pictures']?.['baseLink'] : '',
        },
      });
    } catch (error) {
      showNotification({ message: T.UhOhThereWasAProblem });
    }
  };

  useEffect(() => {
    if (entityType === ENTITY_TYPE.EVENT) {
      getLiveEventEntityData(entityId);
    }
    if (entityType === ENTITY_TYPE.VIDEO) {
      getVideoEntityData(entityId);
    }
    if (entityType === ENTITY_TYPE.SHOWCASE) {
      getAlbumEntityData(entityId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType]);
};
